import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardArrowLeft as BackIcon,
  CheckCircle as CheckCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CalendarToday as CalendarIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import purchaseOrderService from '../../services/purchaseOrderService';

const PurchaseReturnPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });
  
  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState('');
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  
  // Define returnReasons array
  const returnReasons = [
    'Defective Product',
    'Wrong Item Received',
    'Excess Quantity',
    'Item No Longer Needed',
    'Late Delivery',
    'Other'
  ];

  // Get date 15 days ago for filtering
  const getFifteenDaysAgoDate = () => {
    const today = new Date();
    const fifteenDaysAgo = new Date(today);
    fifteenDaysAgo.setDate(today.getDate() - 15);
    return fifteenDaysAgo;
  };

  // Convert date to YYYY-MM-DD format
  const formatDateForComparison = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Check if return order already exists for a purchase order
  const checkExistingReturn = async (orderNumber) => {
    try {
      // Fetch all returned purchase orders
      const returnedOrders = await purchaseOrderService.getReturnedPurchaseOrders();
      const returnOrderNumber = `RETURN-${orderNumber}`;
      
      // Look for a return order with the expected "RETURN-" prefix
      return returnedOrders.find(order => 
        order.orderNumber === returnOrderNumber || 
        (order.orderNumber && order.orderNumber.startsWith(`RETURN-${orderNumber}`))
      );
    } catch (error) {
      console.error('Error checking existing return:', error);
      return null;
    }
  };

  // Fetch all orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching purchase orders for return');
      
      // Get all returned orders first to filter them out
      const returnedOrders = await purchaseOrderService.getReturnedPurchaseOrders();
      const returnedOrderNumbers = new Set(
        returnedOrders
          .map(ro => ro.orderNumber)
          .filter(num => num && num.startsWith('RETURN-'))
          .map(num => num.replace('RETURN-', ''))
      );
      
      const response = await purchaseOrderService.getAllPurchaseOrders({
        page: pagination.page,
        size: 50, // Get a larger set to filter manually
        sort: 'createdAt',
        direction: 'DESC'
      });
      
      // Get date 15 days ago
      const fifteenDaysAgo = getFifteenDaysAgoDate();
      const fifteenDaysAgoStr = formatDateForComparison(fifteenDaysAgo);
      
      console.log(`Filtering orders from the last 15 days (since ${fifteenDaysAgoStr})`);
      
      // Filter orders that can be returned (RECEIVED_COMPLETE status) 
      // AND are within the last 15 days
      // AND don't already have a return processed
      const eligibleOrders = Array.isArray(response.content) ? 
        response.content.filter(order => {
          // 1. Check status is RECEIVED_COMPLETE
          if (order.status !== 'RECEIVED_COMPLETE') return false;
          
          // 2. Check if return already exists
          if (returnedOrderNumbers.has(order.orderNumber)) {
            console.log(`Order ${order.orderNumber} already has a return, excluding.`);
            return false;
          }
          
          // 3. Parse and check date
          let orderDate = null;
          try {
            if (order.orderDate) {
              if (typeof order.orderDate === 'string') {
                orderDate = new Date(order.orderDate);
              } else if (order.orderDate instanceof Date) {
                orderDate = order.orderDate;
              }
            }
          } catch (error) {
            console.error('Error parsing order date:', error);
          }
          
          // If date couldn't be parsed, include the order (fail open)
          if (!orderDate) return true;
          
          // Check if order date is within the last 15 days
          return orderDate >= fifteenDaysAgo;
        }) : 
        [];
      
      console.log(`Found ${eligibleOrders.length} eligible orders for return`);
      
      setOrders(eligibleOrders);
      setFilteredOrders(eligibleOrders.slice(0, pagination.size));
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(eligibleOrders.length / prev.size),
        totalElements: eligibleOrders.length
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      enqueueSnackbar('Failed to load orders. Please try again.', { variant: 'error' });
      
      setOrders([]);
      setFilteredOrders([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 0,
        totalElements: 0
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  // Load orders on component mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Define handleSelectOrder with useCallback to prevent circular dependencies
  const handleSelectOrder = useCallback(async (order) => {
    setLoading(true);
    try {
      // Check if a return already exists for this order
      const existingReturn = await checkExistingReturn(order.orderNumber);
      if (existingReturn) {
        setLoading(false);
        setErrorDialogTitle('Return Already Processed');
        setErrorDialogMessage(`A return has already been processed for purchase order ${order.orderNumber}. You cannot process multiple returns for the same order.`);
        setErrorDialogOpen(true);
        return;
      }

      // Fetch complete order details including items
      const orderDetails = await purchaseOrderService.getPurchaseOrderById(order.id);
      
      setSelectedOrder(orderDetails);
      
      // Get previously returned items for this order
      const previousReturns = await purchaseOrderService.getReturnedItemsForOrder(order.id);
      console.log('Previous returns:', previousReturns);
      
      // Initialize return items from order items with return history
      const items = (orderDetails.items || []).map(item => {
        const quantity = item.quantity || 0;

        // Calculate the effective unit price INCLUDING tax and AFTER discounts,
        // so returns are processed at the exact amount paid (e.g. 150 + 7.50 GST = 157.50).
        // Fallback hierarchy:
        // 1) item.total / quantity (preferred, already includes discount + tax)
        // 2) (item.subtotal + item.taxAmount) / quantity
        // 3) item.unitPrice
        // 4) product cost price
        let effectiveUnitPrice = item.unitPrice || (item.product && item.product.costPrice) || 0;

        if (quantity > 0) {
          const lineTotalWithTax = item.total != null
            ? item.total
            : ((item.subtotal || 0) + (item.taxAmount || 0));

          effectiveUnitPrice = lineTotalWithTax / quantity;
        }

        // Find previous returns for this item
        const previouslyReturned = previousReturns?.find(returnItem => 
          returnItem.productId === (item.product?.id || item.productId)
        );
        
        // Calculate the previously returned quantity
        const previouslyReturnedQty = previouslyReturned ? previouslyReturned.quantity || 0 : 0;
        
        // Calculate available quantity (original - previously returned)
        const availableQty = Math.max(0, (item.quantity || 0) - previouslyReturnedQty);
        
        return {
          ...item,
          returnQuantity: 0,
          selected: false,
          effectiveUnitPrice,
          productName: item.product?.name || 'Unknown Product',
          previouslyReturnedQty,
          availableQty
        };
      });
      
      setReturnItems(items);
      setActiveStep(1);
    } catch (error) {
      console.error('Error fetching order details:', error);
      enqueueSnackbar('Failed to load order details. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Check for orderId in URL search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('orderId');
    
    if (orderId && orders.length > 0) {
      // Find the order in fetched data
      const order = orders.find(o => o.id.toString() === orderId);
      if (order) {
        handleSelectOrder(order);
      } else {
        // If not in the filtered list, try to fetch it directly
        setLoading(true);
        purchaseOrderService.getPurchaseOrderById(orderId)
          .then(orderData => {
            if (orderData && orderData.status === 'RECEIVED_COMPLETE') {
              // Check if the order is within the last 15 days
              const fifteenDaysAgo = getFifteenDaysAgoDate();
              let orderDate = null;
              
              try {
                if (orderData.orderDate) {
                  orderDate = new Date(orderData.orderDate);
                }
              } catch (error) {
                console.error('Error parsing order date:', error);
              }
              
              if (!orderDate || orderDate >= fifteenDaysAgo) {
              handleSelectOrder(orderData);
              } else {
                enqueueSnackbar('Order is older than 15 days and not eligible for return', { variant: 'warning' });
                setLoading(false);
              }
            } else {
              enqueueSnackbar('Order not found or not eligible for return', { variant: 'error' });
              setLoading(false);
            }
          })
          .catch(error => {
            console.error('Error fetching order by ID:', error);
            enqueueSnackbar('Order not found or not eligible for return', { variant: 'error' });
            setLoading(false);
          });
      }
    }
  }, [location.search, orders, handleSelectOrder, enqueueSnackbar]);

  const handleSearch = () => {
    setLoading(true);
    
    setTimeout(() => {
      if (!searchQuery.trim()) {
        setFilteredOrders(orders);
        
        // Update pagination
        setPagination({
          ...pagination,
          totalPages: Math.ceil(orders.length / pagination.size),
          totalElements: orders.length,
          page: 0
        });
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = orders.filter(
          order => 
            (order.orderNumber && order.orderNumber.toLowerCase().includes(query)) || 
            (order.supplier?.name && order.supplier.name.toLowerCase().includes(query))
        );
        
        setFilteredOrders(filtered);
        
        // Update pagination
        setPagination({
          ...pagination,
          totalPages: Math.ceil(filtered.length / pagination.size),
          totalElements: filtered.length,
          page: 0
        });
        
        if (filtered.length === 0) {
          enqueueSnackbar('No orders found with that search term', { variant: 'warning' });
        }
      }
      setLoading(false);
    }, 300);
  };
  
  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value - 1 // MUI Pagination is 1-indexed, our state is 0-indexed
    });
  };

  // Get paginated orders
  const getPaginatedOrders = () => {
    const startIndex = pagination.page * pagination.size;
    const endIndex = startIndex + pagination.size;
    return filteredOrders.slice(startIndex, endIndex);
  };

  const handleNext = () => {
    if (activeStep === 1) {
      const hasSelectedItems = returnItems.some(item => item.selected && item.returnQuantity > 0);
      if (!hasSelectedItems) {
        enqueueSnackbar('Please select at least one item to return', { variant: 'warning' });
        return;
      }
      if (!reason) {
        enqueueSnackbar('Please select a reason for return', { variant: 'warning' });
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReturnQuantityChange = (itemId, value) => {
    const item = returnItems.find(item => item.id === itemId);
    
    if (!item) return;
    
    // Calculate maximum allowable return quantity
    const maxQuantity = item.availableQty || 0;
    
    // Validate and constrain the value
    let newValue = parseInt(value) || 0;
    
    if (newValue < 0) {
      newValue = 0;
      enqueueSnackbar('Return quantity cannot be negative', { variant: 'warning' });
    }
    
    if (newValue > maxQuantity) {
      newValue = maxQuantity;
      enqueueSnackbar(`Cannot return more than ${maxQuantity} items for ${item.productName}. Maximum available quantity reached.`, { variant: 'warning' });
    }
    
    setReturnItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, returnQuantity: newValue, selected: newValue > 0 } 
          : item
      )
    );
  };

  const handleToggleItem = (itemId) => {
    const item = returnItems.find(item => item.id === itemId);
    
    // Don't allow toggling if no available quantity
    if (item.availableQty <= 0) return;
    
    setReturnItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              selected: !item.selected, 
              returnQuantity: !item.selected 
                ? Math.min(1, item.availableQty) // Set to 1 or availableQty (whichever is smaller) when selected
                : 0 
            } 
          : item
      )
    );
  };

  const handleProcessReturn = async () => {
    setLoading(true);
    
    try {
      // Extra safety check: verify a return doesn't already exist for this order
      console.log('Final check for existing return before processing');
      const existingReturn = await checkExistingReturn(selectedOrder.orderNumber);
      
      if (existingReturn) {
        setLoading(false);
        setErrorDialogTitle('Duplicate Return Detected');
        setErrorDialogMessage(`A return has already been processed for purchase order ${selectedOrder.orderNumber}. (Return Order: ${existingReturn.orderNumber})`);
        setErrorDialogOpen(true);
        return;
      }
      
      // Correctly use returnItems from state
      const selectedReturnItems = returnItems
        .filter(item => item.selected && item.returnQuantity > 0)
        .map(item => ({
          productId: item.product?.id || item.productId,
          quantity: item.returnQuantity,
          unitPrice: item.effectiveUnitPrice != null
            ? item.effectiveUnitPrice
            : (item.unitPrice || item.product?.costPrice || 0),
          total: (item.effectiveUnitPrice != null
            ? item.effectiveUnitPrice
            : (item.unitPrice || item.product?.costPrice || 0)) * item.returnQuantity
        }));
      
      // Return data object
      const returnData = {
        items: selectedReturnItems,
        reason: reason,
        notes: notes,
        orderDate: new Date().toISOString().split('T')[0], // Today's date
        total: getReturnTotal()
      };
      
      // Make API call to process return
      const response = await purchaseOrderService.processPurchaseReturn(selectedOrder.id, returnData);
      
      setLoading(false);
      enqueueSnackbar('Return processed successfully! Inventory has been updated.', { variant: 'success' });
      
      // Reset form
      setActiveStep(0);
      setSelectedOrder(null);
      setReturnItems([]);
      setReason('');
      setNotes('');
      
      // Refresh the orders list
      fetchOrders();
    } catch (error) {
      setLoading(false);
      console.error('Error processing return:', error);
      
      let errorMessage = 'Failed to process return.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const getReturnTotal = () => {
    return returnItems
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (
        ((item.effectiveUnitPrice != null
          ? item.effectiveUnitPrice
          : (item.unitPrice || item.product?.costPrice || 0)) * item.returnQuantity)
      ), 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  // Get supplier name from order
  const getSupplierName = (order) => {
    if (!order) return '';
    
    if (order.supplier) {
      if (typeof order.supplier === 'object') {
        return order.supplier.name || 'Unknown Supplier';
      }
      return order.supplier;
    }
    
    return 'Unknown Supplier';
  };

  // Steps for the return process
  const steps = ['Select Purchase Order', 'Select Items to Return', 'Confirm Return'];

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Select a Purchase Order to Return
              </Typography>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  label="Search Orders"
                  variant="outlined"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by order number or supplier name"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleSearch} edge="end">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </Box>
              
              {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No orders found that are eligible for return
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Only orders with "RECEIVED_COMPLETE" status from the last 15 days can be returned.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try checking if you have any completed purchase orders, or try again later.
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Showing orders from the last 15 days that are eligible for return.
                    </Typography>
                  </Box>
                  
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                        {getPaginatedOrders().map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>{order.orderNumber}</TableCell>
                          <TableCell>{formatDate(order.orderDate)}</TableCell>
                          <TableCell>{getSupplierName(order)}</TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                                color={order.status === 'RECEIVED_COMPLETE' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            ₹{(order.total || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSelectOrder(order)}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                  
                  {/* Pagination controls */}
                  {pagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination 
                        count={pagination.totalPages} 
                        page={pagination.page + 1} 
                        onChange={handlePageChange} 
                        color="primary" 
                      />
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Results: {filteredOrders.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Page {pagination.page + 1} of {Math.max(1, pagination.totalPages)}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order: {selectedOrder?.orderNumber || `Order #${selectedOrder?.id}`}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Supplier:</strong> {getSupplierName(selectedOrder)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Order Date:</strong> {formatDate(selectedOrder?.orderDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedOrder?.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong> ₹{(selectedOrder?.total || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              <Typography variant="h6" gutterBottom>
                Select Items to Return
              </Typography>
              
              <TableContainer sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Ordered Qty</TableCell>
                      <TableCell align="right">Previously Returned</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Return Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Return Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems.map((item) => (
                      <TableRow key={item.id} selected={item.selected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={item.selected}
                            onChange={() => handleToggleItem(item.id)}
                            disabled={item.availableQty <= 0}
                          />
                        </TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {item.previouslyReturnedQty > 0 ? (
                            <Chip 
                              label={item.previouslyReturnedQty} 
                              size="small" 
                              color="warning"
                            />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {item.availableQty > 0 ? (
                            <Chip 
                              label={item.availableQty} 
                              size="small" 
                              color="success"
                            />
                          ) : (
                            <Chip 
                              label="0" 
                              size="small" 
                              color="error"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleReturnQuantityChange(item.id, (item.returnQuantity || 0) - 1)}
                              disabled={!item.selected || (item.returnQuantity || 0) <= 0}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              value={item.returnQuantity || 0}
                              onChange={(e) => handleReturnQuantityChange(item.id, parseInt(e.target.value) || 0)}
                              inputProps={{ 
                                min: 0, 
                                max: item.availableQty, 
                                style: { textAlign: 'center', width: '40px' } 
                              }}
                              disabled={!item.selected || item.availableQty <= 0}
                              size="small"
                              variant="outlined"
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleReturnQuantityChange(item.id, (item.returnQuantity || 0) + 1)}
                              disabled={!item.selected || (item.returnQuantity || 0) >= item.availableQty || item.availableQty <= 0}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          ₹{(item.effectiveUnitPrice != null
                            ? item.effectiveUnitPrice
                            : (item.unitPrice || item.product?.costPrice || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          ₹{(
                            (item.effectiveUnitPrice != null
                              ? item.effectiveUnitPrice
                              : (item.unitPrice || item.product?.costPrice || 0)
                            ) * (item.returnQuantity || 0)
                          ).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="reason-label">Return Reason</InputLabel>
                    <Select
                      labelId="reason-label"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      label="Return Reason"
                    >
                      {returnReasons.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Additional Notes"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    variant="outlined"
                    fullWidth
                    placeholder="Please provide any additional details about this return"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Return Summary
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1">
                  <strong>Purchase Order:</strong> {selectedOrder?.orderNumber || `Order #${selectedOrder?.id}`}
                </Typography>
                <Typography variant="body1">
                  <strong>Supplier:</strong> {getSupplierName(selectedOrder)}
                </Typography>
                <Typography variant="body1">
                  <strong>Return Reason:</strong> {reason}
                </Typography>
                {notes && (
                  <Typography variant="body1">
                    <strong>Notes:</strong> {notes}
                  </Typography>
                )}
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Items to Return
              </Typography>
              
              <TableContainer sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Return Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Return Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems
                      .filter(item => item.selected && item.returnQuantity > 0)
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell align="right">{item.returnQuantity}</TableCell>
                          <TableCell align="right">
                            ₹{(item.effectiveUnitPrice != null
                              ? item.effectiveUnitPrice
                              : (item.unitPrice || item.product?.costPrice || 0)).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{(
                              (item.effectiveUnitPrice != null
                                ? item.effectiveUnitPrice
                                : (item.unitPrice || item.product?.costPrice || 0)
                              ) * item.returnQuantity
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
                <Typography variant="h6" sx={{ mr: 2 }}>Total Return Amount:</Typography>
                <Typography variant="h5" color="primary">
                  ₹{getReturnTotal().toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="textSecondary" gutterBottom>
                By confirming this return, you acknowledge that the returned items will be removed from your inventory.
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return <Box>Unknown step</Box>;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom component="h1">
          Process Purchase Return
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              sx={{ mr: 1 }}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NextIcon />}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleProcessReturn}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {loading ? 'Processing...' : 'Process Return'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <ErrorIcon color="error" />
          {errorDialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
            Close
          </Button>
          <Button 
            onClick={() => {
              setErrorDialogOpen(false);
              setActiveStep(0);
              setSelectedOrder(null);
              fetchOrders();
            }} 
            color="primary"
          >
            Go to Order List
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default PurchaseReturnPage; 